import { LogicNameFilter } from "../../../../../utils/constants";
import {
  getIsAdminList,
  getUserTerminationTimestamp,
} from "../../../../../utils/search";
import {
  IntegrationCategory,
  IntegrationsMapping,
} from "../../../integrations/IntegrationsMapping";
import {
  ALL_OPTION_NAME,
  filterName,
  pastTimeOption,
  terminationTimeOption,
} from "../utils";
import { areAllFiltersNotApplied, createEmptyFilters } from "./utils";

const IDPTools = Object.values(IntegrationsMapping)
  .filter((tool) => tool.category === IntegrationCategory.IAM)
  .map((tool) => tool.codeName);

export const getUserSmartFiltersInfo = (
  fetchedEntities,
  appliedFilters,
  setAppliedFilters,
  filterAndOrLogics,
  setFilterAndOrLogics
) => {
  const sources = new Set(fetchedEntities.flatMap((entity) => entity.sources));

  const userMissingIDPApplied = Array.from(sources).every((tool) =>
    IDPTools.includes(tool)
      ? appliedFilters[filterName.EXCLUDE_SOURCE]?.[tool]
      : !appliedFilters[filterName.EXCLUDE_SOURCE]?.[tool]
  );

  const privilegeTools = new Set(
    fetchedEntities.flatMap((entity) => getIsAdminList(entity)).filter(Boolean)
  );

  const privilegedApplied =
    appliedFilters[filterName.PRIVILEGED_IN]?.[ALL_OPTION_NAME] &&
    Array.from(privilegeTools).every(
      (tool) => appliedFilters[filterName.PRIVILEGED_IN]?.[tool]
    ) &&
    filterAndOrLogics[filterName.PRIVILEGED_IN] === LogicNameFilter.LOGIC_OR;

  const inactive90DaysApplied =
    appliedFilters[filterName.LAST_ACTIVE]?.[pastTimeOption.LAST_90_DAYS] &&
    filterAndOrLogics[filterName.LAST_ACTIVE] === LogicNameFilter.TIME_BEFORE;

  const hasConnectedHostsApplied =
    appliedFilters[filterName.CONNECTED_HOSTS_COUNT] &&
    Object.keys(appliedFilters[filterName.CONNECTED_HOSTS_COUNT]).some(
      (key) =>
        key !== ALL_OPTION_NAME &&
        appliedFilters[filterName.CONNECTED_HOSTS_COUNT][key] &&
        parseInt(key) >= 2
    );

  const now = Date.now() / 1000;
  const hasTerminatedUsers = fetchedEntities.some((entity) => {
    const terminationDate = getUserTerminationTimestamp(entity);
    return terminationDate && terminationDate < now;
  });

  const terminatedUsersApplied =
    appliedFilters[filterName.TERMINATION_DATE]?.[
      terminationTimeOption.ALREADY_TERMINATED
    ];

  return [
    {
      text: "Terminated users",
      applied:
        terminatedUsersApplied &&
        areAllFiltersNotApplied({
          appliedFilters: appliedFilters,
          excludedFilterNames: [filterName.TERMINATION_DATE],
        }),
      disabled: !hasTerminatedUsers,
      onClick: () => {
        const updatedFilters = createEmptyFilters(appliedFilters);
        updatedFilters[filterName.TERMINATION_DATE][
          terminationTimeOption.ALREADY_TERMINATED
        ] = true;
        setAppliedFilters(updatedFilters);
      },
    },
    {
      text: "Privileged user inactive for 90 days",
      applied:
        privilegedApplied &&
        inactive90DaysApplied &&
        areAllFiltersNotApplied({
          appliedFilters: appliedFilters,
          excludedFilterNames: [
            filterName.PRIVILEGED_IN,
            filterName.LAST_ACTIVE,
          ],
        }),
      onClick: () => {
        const updatedFilters = createEmptyFilters(appliedFilters);

        updatedFilters[filterName.PRIVILEGED_IN][ALL_OPTION_NAME] = true;
        privilegeTools.forEach((tool) => {
          updatedFilters[filterName.PRIVILEGED_IN][tool] = true;
        });
        updatedFilters[filterName.LAST_ACTIVE][
          pastTimeOption.LAST_90_DAYS
        ] = true;
        setAppliedFilters(updatedFilters);
        setFilterAndOrLogics((prev) => ({
          ...prev,
          [filterName.PRIVILEGED_IN]: LogicNameFilter.LOGIC_OR,
          [filterName.LAST_ACTIVE]: LogicNameFilter.TIME_BEFORE,
        }));
      },
    },
    {
      text: "User missing IDP",
      applied:
        userMissingIDPApplied &&
        areAllFiltersNotApplied({
          appliedFilters: appliedFilters,
          excludedFilterNames: [filterName.EXCLUDE_SOURCE],
        }),
      onClick: () => {
        const updatedFilters = createEmptyFilters(appliedFilters);

        updatedFilters[filterName.EXCLUDE_SOURCE] = Object.fromEntries(
          IDPTools.map((tool) => [tool, true])
        );
        setAppliedFilters(updatedFilters);
      },
    },
    {
      text: "Users with multiple connected hosts",
      applied:
        hasConnectedHostsApplied &&
        areAllFiltersNotApplied({
          appliedFilters: appliedFilters,
          excludedFilterNames: [filterName.CONNECTED_HOSTS_COUNT],
        }),
      onClick: () => {
        const updatedFilters = createEmptyFilters(appliedFilters);

        // Get all possible host counts from the data
        const hostCounts = new Set();
        fetchedEntities.forEach((entity) => {
          const connectedHosts = entity.hosts_ids || [];
          hostCounts.add(connectedHosts.length);
        });

        // Select all counts greater than 1 (users with at least 2 connected hosts)
        const sortedCounts = Array.from(hostCounts).sort((a, b) => a - b);
        const countsWithHosts = sortedCounts.filter((count) => count >= 2);

        countsWithHosts.forEach((count) => {
          updatedFilters[filterName.CONNECTED_HOSTS_COUNT][
            count.toString()
          ] = true;
        });
        setAppliedFilters(updatedFilters);
      },
    },
  ];
};
